import './AuditReportPage.css';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, ConfigProvider, Input, Select, Table, Tooltip } from 'antd';
import { SkeletonRegion, SkeletonTable } from '../common/Skeleton/Skeleton';
import type { ColumnsType, TableProps } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { DownloadOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';

import { dmsAntdTheme } from '../../config/antdTheme';
import { Action, ActionStatus, AuditEventDto, LoginActivityDto } from '../../types/actionTypes';
import { actionHistoryService } from '../../services/actionHistoryService';
import { auditService } from '../../services/auditService';
import { getActionStatusLabel } from '../../utils/actionStatusConfig';
import { actionTypeDisplayName, humanizeActionToken } from '../../utils/actionPresentation';
import ActionStatusIcon from '../common/ActionStatusIcon';

type ReportType = 'actions' | 'platformEvents' | 'loginActivity';

interface SortState {
  field: string;
  order: 'ascend' | 'descend';
}

// ACT-UI-013: the report is exported via a fresh, bounded server request
// (independent of the currently displayed page), never a silent slice of
// only the visible rows. 200 matches the backend's own hard page-size cap.
const EXPORT_MAX_ROWS = 200;

const DEFAULT_SORT: Record<ReportType, SortState> = {
  actions: { field: 'createdAt', order: 'descend' },
  platformEvents: { field: 'occurredAtUtc', order: 'descend' },
  loginActivity: { field: 'loginUtcTime', order: 'descend' },
};

const ACCENT_COLORS = {
  positive: '#28a745',
  negative: '#d40000',
  warning: '#f59e0b',
  neutral: '#8a94a6',
};

const outcomeAccent = (value?: string): string => {
  const normalized = (value || '').toUpperCase();
  if (/(FAIL|ERROR|DENIED|REJECT)/.test(normalized)) return ACCENT_COLORS.negative;
  if (/(SUCCESS|OK|COMPLETE|ALLOW)/.test(normalized)) return ACCENT_COLORS.positive;
  if (/(WARN|PARTIAL|RETRY)/.test(normalized)) return ACCENT_COLORS.warning;
  return ACCENT_COLORS.neutral;
};

const severityAccent = (value?: string): string => {
  const normalized = (value || '').toUpperCase();
  if (/(CRITICAL|ERROR|HIGH)/.test(normalized)) return ACCENT_COLORS.negative;
  if (/(WARN|MEDIUM)/.test(normalized)) return ACCENT_COLORS.warning;
  return ACCENT_COLORS.neutral;
};

const AccentCell: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span className="audit-report-page__accent-cell">
    <span className="audit-report-page__accent-dot" style={{ backgroundColor: color }} aria-hidden="true" />
    {label}
  </span>
);

const formatDateTime = (value?: string): string => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const parseLocalDate = (value: string): Date | null => {
  const isoMatch = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return parsed.getFullYear() === Number(year)
      && parsed.getMonth() === Number(month) - 1
      && parsed.getDate() === Number(day)
      ? parsed
      : null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toDateSearchRange = (value: string): { fromUtc: string; toUtc: string } | null => {
  const trimmed = value.trim();
  if (!trimmed || !/[0-9]/.test(trimmed) || !/[/-]/.test(trimmed)) {
    return null;
  }
  const parsed = parseLocalDate(trimmed);
  if (!parsed) {
    return null;
  }

  const hasExplicitTime = /\d{1,2}:\d{2}/.test(trimmed);
  const from = hasExplicitTime
    ? parsed
    : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  const to = hasExplicitTime
    ? new Date(from.getTime() + 1000)
    : new Date(from.getFullYear(), from.getMonth(), from.getDate() + 1);
  return { fromUtc: from.toISOString(), toUtc: to.toISOString() };
};

const toCsvValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  const raw = String(value).replace(/"/g, '""');
  return `"${raw}"`;
};

const downloadCsv = (fileName: string, headers: string[], rows: Array<Array<unknown>>) => {
  const csv = [headers.map((header) => toCsvValue(header)).join(','), ...rows.map((row) => row.map(toCsvValue).join(','))]
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const sortParamOf = (sort: SortState): string => `${sort.field},${sort.order === 'ascend' ? 'asc' : 'desc'}`;

const AuditReportPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [actions, setActions] = useState<Action[]>([]);
  const [events, setEvents] = useState<AuditEventDto[]>([]);
  const [loginActivity, setLoginActivity] = useState<LoginActivityDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selectedReport, setSelectedReport] = useState<ReportType>('actions');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT.actions);
  const requestId = useRef(0);
  const loadedReportsRef = useRef<Set<ReportType>>(new Set());

  const reportOptions = useMemo(
    () => [
      { label: t('audit.report.tabs.actions'), value: 'actions' as const },
      { label: t('audit.report.tabs.platformEvents'), value: 'platformEvents' as const },
      { label: t('audit.report.tabs.loginActivity'), value: 'loginActivity' as const },
    ],
    [t]
  );

  const loadReport = useCallback(async () => {
    const currentRequestId = ++requestId.current;
    const reportAtStart = selectedReport;
    const alreadyLoaded = loadedReportsRef.current.has(reportAtStart);
    setLoading(!alreadyLoaded);
    setRefreshing(alreadyLoaded);
    setError(null);
    try {
      const dateRange = toDateSearchRange(query);
      const search = dateRange ? undefined : query.trim() || undefined;
      const baseQuery = { page, size: pageSize, search, sort: sortParamOf(sort), ...(dateRange ?? {}) };

      if (reportAtStart === 'actions') {
        const result = await actionHistoryService.fetchActions(baseQuery);
        if (currentRequestId === requestId.current) {
          setActions(result.content);
          setTotal(result.totalElements);
        }
      } else if (reportAtStart === 'platformEvents') {
        const result = await auditService.fetchAuditEvents(baseQuery);
        if (currentRequestId === requestId.current) {
          setEvents(result.content);
          setTotal(result.totalElements);
        }
      } else {
        const result = await auditService.fetchLoginActivity(baseQuery);
        if (currentRequestId === requestId.current) {
          setLoginActivity(result.content);
          setTotal(result.totalElements);
        }
      }
      if (currentRequestId === requestId.current) {
        loadedReportsRef.current.add(reportAtStart);
      }
    } catch {
      if (currentRequestId === requestId.current) {
        setError(t('audit.report.errors.loadFailed'));
        loadedReportsRef.current.add(reportAtStart);
      }
    } finally {
      if (currentRequestId === requestId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [query, selectedReport, page, pageSize, sort, t]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setPage(0);
  };

  const handleReportChange = (value: ReportType) => {
    setSelectedReport(value);
    setPage(0);
    setSort(DEFAULT_SORT[value]);
  };

  const handleExport = useCallback(async () => {
    setExporting(true);
    setError(null);
    try {
      const dateRange = toDateSearchRange(query);
      const search = dateRange ? undefined : query.trim() || undefined;
      const exportQuery = { page: 0, size: EXPORT_MAX_ROWS, search, sort: sortParamOf(sort), ...(dateRange ?? {}) };

      if (selectedReport === 'actions') {
        const result = await actionHistoryService.fetchActions(exportQuery);
        downloadCsv(
          'actions-report.csv',
          ['id', 'actionType', 'username', 'status', 'createdAt', 'completedAt', 'totalRtus', 'comment'],
          result.content.map((action) => [
            action.id,
            action.actionType,
            action.username ?? action.userId,
            action.status,
            action.createdAt,
            action.completedAt,
            action.totalRtus ?? action.actionDetails.length,
            action.comment,
          ])
        );
      } else if (selectedReport === 'platformEvents') {
        const result = await auditService.fetchAuditEvents(exportQuery);
        downloadCsv(
          'platform-events-report.csv',
          [
            'id', 'occurredAtUtc', 'eventType', 'outcome', 'severity', 'sourceComponent',
            'actorName', 'rtuId', 'actionId', 'stage', 'errorCode',
          ],
          result.content.map((event) => [
            event.id, event.occurredAtUtc, event.eventType, event.outcome, event.severity,
            event.sourceComponent, event.actorName, event.rtuId, event.actionId, event.stage, event.errorCode,
          ])
        );
      } else {
        const result = await auditService.fetchLoginActivity(exportQuery);
        downloadCsv(
          'login-activity-report.csv',
          ['rtuId', 'rtuName', 'username', 'sourceIp', 'loginType', 'loginUtcTime'],
          result.content.map((entry) => [
            entry.rtuId, entry.rtuName, entry.username, entry.sourceIp, entry.loginType, entry.loginUtcTime,
          ])
        );
      }
    } catch {
      setError(t('audit.report.errors.exportFailed', { defaultValue: 'Failed to export the report.' }));
    } finally {
      setExporting(false);
    }
  }, [query, selectedReport, sort, t]);

  const sortOrderFor = (field: string): 'ascend' | 'descend' | undefined => (sort.field === field ? sort.order : undefined);

  const actionColumns = useMemo<ColumnsType<Action>>(() => [
    { title: t('audit.actions.columns.id'), dataIndex: 'id', key: 'id', width: 80, sorter: true, sortOrder: sortOrderFor('id') },
    {
      title: t('audit.actions.columns.actionType'),
      dataIndex: 'actionType',
      key: 'actionType',
      width: 170,
      sorter: true,
      sortOrder: sortOrderFor('actionType'),
      render: (value: string) => actionTypeDisplayName(value, t),
    },
    {
      title: t('audit.actions.columns.username'),
      dataIndex: 'username',
      key: 'username',
      width: 150,
      render: (_value, row) => row.username ?? row.userId ?? '—',
    },
    {
      title: t('audit.actions.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 160,
      sorter: true,
      sortOrder: sortOrderFor('status'),
      render: (value: ActionStatus) => (
        <span className="audit-report-page__status-cell">
          <ActionStatusIcon status={value} />
          <span>{getActionStatusLabel(value, t)}</span>
        </span>
      ),
    },
    {
      title: t('audit.actions.columns.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      sorter: true,
      sortOrder: sortOrderFor('createdAt'),
      render: formatDateTime,
    },
    {
      title: t('audit.actions.columns.completedAt'),
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 180,
      sorter: true,
      sortOrder: sortOrderFor('completedAt'),
      render: formatDateTime,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, sort]);

  const eventColumns = useMemo<ColumnsType<AuditEventDto>>(() => [
    { title: t('audit.report.columns.id'), dataIndex: 'id', key: 'id', width: 80, sorter: true, sortOrder: sortOrderFor('id') },
    {
      title: t('audit.report.columns.occurredAtUtc'),
      dataIndex: 'occurredAtUtc',
      key: 'occurredAtUtc',
      width: 180,
      sorter: true,
      sortOrder: sortOrderFor('occurredAtUtc'),
      render: formatDateTime,
    },
    {
      title: t('audit.report.columns.eventType'),
      dataIndex: 'eventType',
      key: 'eventType',
      width: 170,
      sorter: true,
      sortOrder: sortOrderFor('eventType'),
      render: humanizeActionToken,
    },
    {
      title: t('audit.report.columns.outcome'),
      dataIndex: 'outcome',
      key: 'outcome',
      width: 140,
      sorter: true,
      sortOrder: sortOrderFor('outcome'),
      render: (value: string) => <AccentCell color={outcomeAccent(value)} label={humanizeActionToken(value)} />,
    },
    {
      title: t('audit.report.columns.severity'),
      dataIndex: 'severity',
      key: 'severity',
      width: 120,
      sorter: true,
      sortOrder: sortOrderFor('severity'),
      render: (value: string) => <AccentCell color={severityAccent(value)} label={humanizeActionToken(value)} />,
    },
    {
      title: t('audit.report.columns.sourceComponent'),
      dataIndex: 'sourceComponent',
      key: 'sourceComponent',
      width: 170,
      sorter: true,
      sortOrder: sortOrderFor('sourceComponent'),
      render: (value?: string) => value || '—',
    },
    {
      title: t('audit.report.columns.actorName'),
      dataIndex: 'actorName',
      key: 'actorName',
      width: 160,
      render: (_value, row) => row.actorName || row.actorId || '—',
    },
    { title: t('audit.report.columns.rtuId'), dataIndex: 'rtuId', key: 'rtuId', width: 100, render: (value?: number) => value ?? '—' },
    { title: t('audit.report.columns.actionId'), dataIndex: 'actionId', key: 'actionId', width: 100, render: (value?: number) => value ?? '—' },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, sort]);

  const loginColumns = useMemo<ColumnsType<LoginActivityDto>>(() => [
    { title: t('audit.report.columns.rtuId'), dataIndex: 'rtuId', key: 'rtuId', width: 100, sorter: true, sortOrder: sortOrderFor('rtuId') },
    { title: t('audit.report.columns.rtuName'), dataIndex: 'rtuName', key: 'rtuName', width: 180, sorter: true, sortOrder: sortOrderFor('rtuName') },
    { title: t('audit.report.columns.username'), dataIndex: 'username', key: 'username', width: 150, sorter: true, sortOrder: sortOrderFor('username') },
    { title: t('audit.report.columns.sourceIp'), dataIndex: 'sourceIp', key: 'sourceIp', width: 150, sorter: true, sortOrder: sortOrderFor('sourceIp') },
    {
      title: t('audit.report.columns.loginType'),
      dataIndex: 'loginType',
      key: 'loginType',
      width: 130,
      sorter: true,
      sortOrder: sortOrderFor('loginType'),
      render: humanizeActionToken,
    },
    {
      title: t('audit.report.columns.loginUtcTime'),
      dataIndex: 'loginUtcTime',
      key: 'loginUtcTime',
      width: 180,
      sorter: true,
      sortOrder: sortOrderFor('loginUtcTime'),
      render: formatDateTime,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, sort]);

  const activeColumns: ColumnsType<any> = selectedReport === 'actions'
    ? actionColumns
    : selectedReport === 'platformEvents'
      ? eventColumns
      : loginColumns;

  const activeData: any[] = selectedReport === 'actions' ? actions : selectedReport === 'platformEvents' ? events : loginActivity;

  const rowKey = selectedReport === 'loginActivity'
    ? (row: LoginActivityDto, index?: number) => `${row.rtuId}-${row.loginUtcTime}-${index}`
    : 'id';

  const handleTableChange: TableProps<any>['onChange'] = (paginationConfig, _filters, sorter) => {
    const nextPage = (paginationConfig.current ?? 1) - 1;
    const nextSize = paginationConfig.pageSize ?? pageSize;
    const single = (Array.isArray(sorter) ? sorter[0] : sorter) as SorterResult<any> | undefined;
    const nextSort: SortState = single && single.order && single.field
      ? { field: String(single.field), order: single.order }
      : DEFAULT_SORT[selectedReport];
    setPage(nextPage);
    setPageSize(nextSize);
    setSort(nextSort);
  };

  const emptyMessage = query.trim()
    ? t('audit.report.empty.noResults')
    : selectedReport === 'actions'
      ? t('audit.report.empty.actions')
      : selectedReport === 'platformEvents'
        ? t('audit.report.empty.events')
        : t('audit.report.empty.loginActivity');

  const showSkeleton = loading;
  const skeletonColumns = selectedReport === 'actions'
    ? [0.5, 1.4, 1, 0.8, 1.2, 1.2]
    : selectedReport === 'platformEvents'
      ? [0.5, 1.2, 1, 0.8, 0.8, 1, 1, 0.6, 0.6]
      : [0.6, 1, 1, 1, 0.9, 1.1];

  return (
    <ConfigProvider direction={i18n.dir()} theme={dmsAntdTheme}>
      <div className="audit-report-page">
        <Card size="small" className="audit-report-page__card">
          <div className="audit-report-page__toolbar">
            <Input
              value={query}
              onChange={(event) => handleQueryChange(event.target.value)}
              placeholder={t('audit.report.searchPlaceholder')}
              aria-label={t('audit.report.searchPlaceholder')}
              prefix={<SearchOutlined />}
              allowClear
              className="audit-report-page__search"
            />
            <Select
              value={selectedReport}
              options={reportOptions}
              onChange={handleReportChange}
              className="audit-report-page__report-select"
              aria-label={t('audit.report.reportType', { defaultValue: 'Report type' })}
              virtual={false}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => { void loadReport(); }}
              aria-label={t('audit.report.refresh')}
            >
              {t('audit.report.refresh')}
            </Button>
            <Tooltip title={t('audit.report.exportScopeHint', { defaultValue: `Exports up to the first ${EXPORT_MAX_ROWS} matching rows.`, count: EXPORT_MAX_ROWS })}>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => { void handleExport(); }}
                loading={exporting}
                aria-label={t('audit.report.exportCsv')}
              >
                {t('audit.report.exportCsv')}
              </Button>
            </Tooltip>
          </div>

          {error ? (
            <Alert
              type="error"
              showIcon
              message={error}
              action={(
                <Button size="small" onClick={() => { void loadReport(); }}>
                  {t('audit.report.errors.retry', { defaultValue: 'Retry' })}
                </Button>
              )}
              className="audit-report-page__alert"
            />
          ) : null}

          {showSkeleton ? (
            <SkeletonRegion label={t('audit.report.loading', { defaultValue: 'Loading report' })} className="audit-report-page__skeleton">
              <SkeletonTable columns={skeletonColumns} rows={10} />
            </SkeletonRegion>
          ) : (
            <Table
              rowKey={rowKey}
              columns={activeColumns}
              dataSource={activeData}
              loading={{ spinning: refreshing }}
              onChange={handleTableChange}
              size="small"
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: emptyMessage }}
              pagination={{
                current: page + 1,
                pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50'],
              }}
            />
          )}
        </Card>
      </div>
    </ConfigProvider>
  );
};

export default AuditReportPage;
